// PlacesAutocomplete.js
import React, { useState, useEffect, useRef, useCallback } from "react";

const PlacesAutocomplete = ({
  value,
  onChange,
  onSelect,
  error,
  disabled,
  placeholder = "LOCATION *",
  useCurrentLocation,
  className = "",
}) => {
  const [autocomplete, setAutocomplete] = useState(null);
  const [placeSelected, setPlaceSelected] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const inputRef = useRef(null);
  const autocompleteInitialized = useRef(false);
  const scriptLoading = useRef(false);

  // Safe check for Google Maps
  const isGoogleMapsReady = useCallback(() => {
    return !!(window.google && window.google.maps && window.google.maps.places);
  }, []);

  // Load Google Maps script
  const loadGoogleMapsScript = useCallback(() => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (isGoogleMapsReady()) {
        setScriptLoaded(true);
        resolve();
        return;
      }

      // Check if script is already loading
      if (scriptLoading.current) {
        const checkInterval = setInterval(() => {
          if (isGoogleMapsReady()) {
            clearInterval(checkInterval);
            setScriptLoaded(true);
            resolve();
          }
        }, 100);

        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error("Google Maps loading timeout"));
        }, 10000);
        return;
      }

      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        reject(new Error("Google Maps API key is missing"));
        return;
      }

      scriptLoading.current = true;
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        setTimeout(() => {
          if (isGoogleMapsReady()) {
            setScriptLoaded(true);
            scriptLoading.current = false;
            resolve();
          } else {
            scriptLoading.current = false;
            reject(new Error("Google Maps failed to initialize"));
          }
        }, 100);
      };

      script.onerror = () => {
        scriptLoading.current = false;
        reject(new Error("Failed to load Google Maps script"));
      };

      document.head.appendChild(script);
    });
  }, [isGoogleMapsReady]);

  // Initialize autocomplete
  const initializeAutocomplete = useCallback(async () => {
    if (
      !inputRef.current ||
      autocompleteInitialized.current ||
      useCurrentLocation
    ) {
      return;
    }

    try {
      // Ensure Google Maps is loaded
      if (!isGoogleMapsReady()) {
        await loadGoogleMapsScript();
      }

      // Clear any existing autocomplete
      if (autocomplete && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }

      const autocompleteInstance = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ["(regions)"],
          fields: [
            "formatted_address",
            "geometry",
            "address_components",
            "types",
          ],
        },
      );

      autocompleteInstance.addListener("place_changed", () => {
        setPlaceSelected(true);
        const place = autocompleteInstance.getPlace();

        if (!place.geometry) {
          onSelect({
            location: "",
            coordinates: "",
            city: "",
            country: "",
            error: "Please select a valid location from the dropdown",
          });
          return;
        }

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        let city = "";
        let state = "";
        let country = "";

        for (const component of place.address_components) {
          if (component.types.includes("locality")) {
            city = component.long_name;
          }
          if (component.types.includes("administrative_area_level_1")) {
            state = component.long_name;
          }
          if (component.types.includes("administrative_area_level_2")) {
            if (!city) city = component.long_name;
          }
          if (component.types.includes("country")) {
            country = component.long_name;
          }
        }

        const generalLocation = [city, state, country]
          .filter((part) => part)
          .join(", ");

        onSelect({
          location: generalLocation,
          coordinates: `(${lat},${lng})`,
          city: city,
          country: country,
          error: "",
        });
      });

      setAutocomplete(autocompleteInstance);
      autocompleteInitialized.current = true;
    } catch (err) {
      console.error("Error initializing Google Maps autocomplete:", err);
    }
  }, [
    isGoogleMapsReady,
    loadGoogleMapsScript,
    onSelect,
    autocomplete,
    useCurrentLocation,
  ]);

  // Initialize when component mounts or dependencies change
  useEffect(() => {
    if (!useCurrentLocation && value && !autocompleteInitialized.current) {
      initializeAutocomplete();
    }
  }, [initializeAutocomplete, useCurrentLocation, value]);

  // Re-initialize when useCurrentLocation changes
  useEffect(() => {
    if (
      !useCurrentLocation &&
      inputRef.current &&
      !autocompleteInitialized.current
    ) {
      const timer = setTimeout(() => {
        initializeAutocomplete();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [useCurrentLocation, initializeAutocomplete]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (autocomplete && window.google?.maps?.event) {
        try {
          window.google.maps.event.clearInstanceListeners(autocomplete);
          autocompleteInitialized.current = false;
        } catch (err) {
          console.error("Error clearing autocomplete listeners:", err);
        }
      }
    };
  }, [autocomplete]);

  const handleBlur = () => {
    if (useCurrentLocation || !isGoogleMapsReady()) return;

    const val = inputRef.current?.value.trim();
    if (!val || placeSelected) {
      setPlaceSelected(false);
      return;
    }

    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: val }, (results, status) => {
        if (status === "OK" && results[0]?.geometry) {
          const loc = results[0].geometry.location;

          let city = "";
          let state = "";
          let country = "";

          for (const component of results[0].address_components) {
            if (component.types.includes("locality")) {
              city = component.long_name;
            }
            if (component.types.includes("administrative_area_level_1")) {
              state = component.long_name;
            }
            if (component.types.includes("country")) {
              country = component.long_name;
            }
          }

          const generalLocation = [city, state, country]
            .filter((part) => part)
            .join(", ");

          onSelect({
            location: generalLocation,
            coordinates: `(${loc.lat()},${loc.lng()})`,
            city: city,
            country: country,
            error: "",
          });
        } else {
          onSelect({
            location: val,
            coordinates: "",
            city: "",
            country: "",
            error:
              "Please select a valid location from the dropdown or type a correct address",
          });
        }
      });
    } catch (err) {
      console.error("Error during geocoding:", err);
      onSelect({
        location: val,
        coordinates: "",
        city: "",
        country: "",
        error: "Location service temporarily unavailable",
      });
    }
    setPlaceSelected(false);
  };

  if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return (
      <div className="alert alert-danger">
        Google Maps API key is missing. Please set REACT_APP_GOOGLE_MAPS_API_KEY
        in your .env file
      </div>
    );
  }

  return (
    <div className="places-autocomplete-container">
      <div className="position-relative">
        <input
          ref={inputRef}
          type="text"
          className={`form-control ${className}`}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            if (!useCurrentLocation) {
              onChange(e.target.value);
              onSelect({
                location: e.target.value,
                coordinates: "",
                city: "",
                country: "",
                error: "",
              });
            }
          }}
          onBlur={handleBlur}
          disabled={disabled || useCurrentLocation}
          autoComplete="off"
          aria-label="Location search"
        />
      </div>
      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
};

export default PlacesAutocomplete;
